// src/PersonasPage.jsx - VERSIÓN MEJORADA CON RESPONSIVE + DASHBOARD + EXCEL EXPORT
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadAuth, clearAuth } from "./auth";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import api from "./Api";

/* ---------- Toolbar personalizada ---------- */
function CustomToolbar({ onAdd, onEdit, onDelete, onExportExcel }) {
  return (
    <GridToolbarContainer sx={{ p: 1 }} className="no-print">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, flexWrap: 'wrap' }}>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={onAdd}
          sx={{ background: "linear-gradient(90deg, #667eea, #764ba2)", mb: { xs: 1, sm: 0 } }}
        >
          Agregar
        </Button>
        <Button startIcon={<EditIcon />} variant="outlined" onClick={onEdit} sx={{ mb: { xs: 1, sm: 0 } }}>
          Editar
        </Button>
        <Button
          startIcon={<DeleteIcon />}
          color="error"
          variant="outlined"
          onClick={onDelete}
          sx={{ mb: { xs: 1, sm: 0 } }}
        >
          Borrar
        </Button>
        <GridToolbarQuickFilter sx={{ ml: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' } }} placeholder="Filtra visible…" />
      </Stack>
      <Button
        startIcon={<DownloadIcon />}
        variant="outlined"
        onClick={onExportExcel}
        sx={{ color: "#4caf50", borderColor: "#4caf50", "&:hover": { borderColor: "#45a049", bgcolor: "rgba(76, 175, 80, 0.1)" } }}
      >
        Exportar a Excel
      </Button>
    </GridToolbarContainer>
  );
}

/* ---------- Validadores ---------- */
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(email || "").trim());

export default function PersonasPage({ onLogout }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Usuario actual
  const auth = loadAuth();
  const userName = auth?.user?.nombre || "Usuario";

  function handleLogout() {
    if (typeof onLogout === "function") {
      onLogout();
    } else {
      clearAuth();
    }
    navigate("/", { replace: true });
  }

  /* ---------- Estado ---------- */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    este_mes: 0,
    monto_total: 0,
    tipo_comun: "otro",
  });

  // Paginación
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Búsqueda y filtros
  const [q, setQ] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  // Selección de fila
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  // Modal crear/editar
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: "",
    direccion: "",
    curp: "",
    telefono: "",
    email: "",
    comentarios: "",
    tipo_apoyo: "otro",
    monto: "",
    fecha_apoyo: "",
  });

  // Errores de inputs
  const [errors, setErrors] = useState({
    curp: "",
    email: "",
  });

  // Toast
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  const isCurpValid = !errors.curp && form.curp.trim().length === 18;
  const openToast = (msg, severity = "info") => setToast({ open: true, msg, severity });
  const closeToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((t) => ({ ...t, open: false }));
  };
  const parseAxiosError = (e) => {
    const apiMsg = e?.response?.data?.error || e?.response?.data?.message;
    if (apiMsg) return apiMsg;
    if (e?.message) return e.message;
    return "Ocurrió un error inesperado";
  };

  /* ---------- Tipos de apoyo ---------- */
  const tiposApoyo = [
    { value: "despensa", label: "Despensa", color: "#4caf50" },
    { value: "medicamento", label: "Medicamento", color: "#2196f3" },
    { value: "economico", label: "Económico", color: "#ff9800" },
    { value: "ropa", label: "Ropa", color: "#9c27b0" },
    { value: "muebles", label: "Muebles", color: "#795548" },
    { value: "otro", label: "Otro", color: "#607d8b" },
  ];

  const getTipoLabel = (tipo) => {
    const found = tiposApoyo.find((t) => t.value === tipo);
    return found ? found.label : "Otro";
  };

  const getTipoColor = (tipo) => {
    const found = tiposApoyo.find((t) => t.value === tipo);
    return found ? found.color : "#607d8b";
  };

  /* ---------- Columnas Grid ---------- */
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 70 },
      { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 160 },
      {
        field: "direccion",
        headerName: "Dirección",
        flex: 1,
        minWidth: 200,
        hide: isMobile, // Ocultar en móvil
      },
      {
        field: "curp",
        headerName: "CURP",
        width: 150,
        hide: isMobile, // Ocultar en móvil
      },
      {
        field: "telefono",
        headerName: "Teléfono",
        width: 140,
        hide: isTablet, // Ocultar en tablet y móvil
      },
      {
        field: "tipo_apoyo",
        headerName: "Tipo de Apoyo",
        width: 140,
        renderCell: (params) => (
          <Chip
            label={getTipoLabel(params.value)}
            size="small"
            sx={{
              bgcolor: getTipoColor(params.value),
              color: "white",
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        field: "monto",
        headerName: "Monto",
        width: 120,
        hide: isMobile,
        renderCell: (params) =>
          params.value > 0 ? `$${parseFloat(params.value).toFixed(2)}` : "-",
      },
      { field: "fecha_apoyo", headerName: "Fecha apoyo", width: 140 },
    ],
    [isMobile, isTablet]
  );

  /* ---------- Carga (GET) ---------- */
  async function load() {
    try {
      const { page, pageSize } = paginationModel;
      const res = await api.get("", {
        params: { entity: "personas", q, tipo: filtroTipo, page: page + 1, pageSize },
      });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      openToast(`Error al cargar: ${parseAxiosError(e)}`, "error");
    }
  }

  /* ---------- Cargar estadísticas ---------- */
  async function loadStats() {
    try {
      const res = await api.get("", {
        params: { entity: "personas", action: "stats" },
      });
      if (res.data?.success) {
        setStats(res.data.stats);
      }
    } catch (e) {
      console.error("Error al cargar estadísticas:", e);
    }
  }

  useEffect(() => {
    load();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, filtroTipo]);

  const doSearch = async () => {
    setPaginationModel((m) => ({ ...m, page: 0 }));
    await load();
  };

  /* ---------- Handlers de UI ---------- */
  const selectedRow = rows.find((r) => r.id === rowSelectionModel[0]) || null;

  const openCreate = () => {
    setEditing(null);
    setForm({
      nombre: "",
      direccion: "",
      curp: "",
      telefono: "",
      email: "",
      comentarios: "",
      tipo_apoyo: "otro",
      monto: "",
      fecha_apoyo: "",
    });
    setErrors({ curp: "", email: "" });
    setOpen(true);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({
      nombre: selectedRow.nombre || "",
      direccion: selectedRow.direccion || "",
      curp: selectedRow.curp || "",
      telefono: selectedRow.telefono || "",
      email: selectedRow.email || "",
      comentarios: selectedRow.comentarios || "",
      tipo_apoyo: selectedRow.tipo_apoyo || "otro",
      monto: selectedRow.monto ? String(selectedRow.monto) : "",
      fecha_apoyo: (selectedRow.fecha_apoyo || "").slice(0, 10),
    });
    setErrors({ curp: "", email: "" });
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  /* ---------- Validaciones de inputs ---------- */
  const validateCurpLen = (val) =>
    (val || "").trim().length === 18 ? "" : "La CURP debe tener exactamente 18 caracteres";

  function handleCurpChange(e) {
    let val = e.target.value.toUpperCase().replace(/\s/g, "");
    if (val.length > 18) val = val.slice(0, 18);
    setForm((f) => ({ ...f, curp: val }));
    setErrors((er) => ({ ...er, curp: val ? validateCurpLen(val) : "" }));
  }

  function handleEmailChange(e) {
    const val = e.target.value;
    setForm((f) => ({ ...f, email: val }));
    setErrors((er) => ({
      ...er,
      email: val && !isValidEmail(val) ? "Correo no válido" : "",
    }));
  }

  const TEL_MAX = 10;

  function handleTelefonoChange(e) {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > TEL_MAX) val = val.slice(0, TEL_MAX);
    setForm((f) => ({ ...f, telefono: val }));
  }

  /* ---------- Guardar (POST/PUT) ---------- */
  async function save() {
    if (!form.nombre || !form.direccion || !form.curp) {
      openToast("Nombre, dirección y CURP son obligatorios", "warning");
      return;
    }
    if (form.curp.length !== 18) {
      openToast("⚠️ La CURP debe tener exactamente 18 caracteres", "warning");
      setErrors((er) => ({ ...er, curp: "La CURP debe tener exactamente 18 caracteres" }));
      return;
    }
    if (form.email && !isValidEmail(form.email)) {
      openToast("Correo no válido", "warning");
      setErrors((er) => ({ ...er, email: "Correo no válido" }));
      return;
    }

    try {
      const payload = {
        ...form,
        monto: form.monto ? parseFloat(form.monto) : 0,
      };

      if (!editing) {
        await api.post("", payload, { params: { entity: "personas" } });
        openToast("✅ Persona creada", "success");
      } else {
        await api.put("", { id: editing.id, ...payload }, { params: { entity: "personas" } });
        openToast("✏️ Persona actualizada", "success");
      }
      closeDialog();
      await load();
      await loadStats(); // Recargar estadísticas
    } catch (e) {
      console.error(e);
      openToast(`Error al guardar: ${parseAxiosError(e)}`, "error");
    }
  }

  /* ---------- Eliminar (DELETE) ---------- */
  async function remove() {
    if (!selectedRow) return;
    if (!window.confirm(`¿Eliminar a ${selectedRow.nombre}?`)) return;
    try {
      await api.delete("", {
        data: { id: selectedRow.id },
        params: { entity: "personas" },
      });
      openToast("🗑️ Persona eliminada", "success");
      await load();
      await loadStats(); // Recargar estadísticas
    } catch (e) {
      console.error(e);
      openToast(`Error al eliminar: ${parseAxiosError(e)}`, "error");
    }
  }

  /* ---------- Exportar a Excel con formato profesional ---------- */
  const exportToExcel = () => {
    try {
      // Preparar datos para Excel
      const excelData = rows.map((row, index) => ({
        "No.": index + 1,
        "ID": row.id,
        "Nombre": row.nombre,
        "Dirección": row.direccion,
        "CURP": row.curp,
        "Teléfono": row.telefono,
        "Email": row.email,
        "Tipo de Apoyo": getTipoLabel(row.tipo_apoyo),
        "Monto": row.monto > 0 ? `$${parseFloat(row.monto).toFixed(2)}` : "-",
        "Fecha de Apoyo": row.fecha_apoyo || "-",
        "Comentarios": row.comentarios,
      }));

      // Agregar fila de totales
      const totalMonto = rows.reduce((sum, row) => sum + parseFloat(row.monto || 0), 0);
      excelData.push({
        "No.": "",
        "ID": "",
        "Nombre": "",
        "Dirección": "",
        "CURP": "",
        "Teléfono": "",
        "Email": "",
        "Tipo de Apoyo": "TOTAL",
        "Monto": `$${totalMonto.toFixed(2)}`,
        "Fecha de Apoyo": "",
        "Comentarios": `Total de registros: ${rows.length}`,
      });

      // Crear libro de Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Configurar anchos de columna
      const columnWidths = [
        { wch: 5 },  // No.
        { wch: 8 },  // ID
        { wch: 30 }, // Nombre
        { wch: 40 }, // Dirección
        { wch: 20 }, // CURP
        { wch: 15 }, // Teléfono
        { wch: 25 }, // Email
        { wch: 15 }, // Tipo de Apoyo
        { wch: 15 }, // Monto
        { wch: 15 }, // Fecha de Apoyo
        { wch: 50 }, // Comentarios
      ];
      ws['!cols'] = columnWidths;

      // Agregar hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, "Apoyos Otorgados");

      // Generar nombre de archivo con fecha
      const fecha = new Date().toISOString().split('T')[0];
      const fileName = `Apoyos_${filtroTipo !== 'todos' ? getTipoLabel(filtroTipo) + '_' : ''}${fecha}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(wb, fileName);

      openToast("✅ Archivo Excel descargado", "success");
    } catch (error) {
      console.error("Error al exportar:", error);
      openToast("❌ Error al exportar a Excel", "error");
    }
  };

  /* ---------- Render ---------- */
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        backgroundColor: "#0f172a",
        p: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          margin: "0 auto",
          bgcolor: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.15)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          color: "white",
          boxShadow: "0 10px 30px rgba(0,0,0,.35)",
        }}
      >
        {/* Header con info de usuario */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3, gap: 2 }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <PersonIcon sx={{ color: "#79e9d0" }} />
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                Bienvenido/a
              </Typography>
            </Stack>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
              Sistema de Gestión de Apoyos
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate("/change-password")}
              sx={{
                borderColor: "rgba(255,255,255,.4)",
                color: "white",
                "&:hover": { borderColor: "white", background: "rgba(255,255,255,.1)" },
              }}
            >
              Cambiar contraseña
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleLogout}
              sx={{
                borderColor: "rgba(255,255,255,.4)",
                color: "white",
                "&:hover": { borderColor: "white", background: "rgba(255,255,255,.1)" },
              }}
            >
              Salir
            </Button>
          </Stack>
        </Stack>

        {/* Dashboard - Estadísticas */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Total de Apoyos
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "white" }}>
                      {stats.total}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)" }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Este Mes
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "white" }}>
                      {stats.este_mes}
                    </Typography>
                  </Box>
                  <CalendarMonthIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)" }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Monto Total
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "white" }}>
                      ${parseFloat(stats.monto_total).toLocaleString()}
                    </Typography>
                  </Box>
                  <AttachMoneyIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)" }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                      Más Común
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "white" }}>
                      {getTipoLabel(stats.tipo_comun)}
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ fontSize: 48, color: "rgba(255,255,255,0.3)" }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtros rápidos */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
        >
          <Chip
            label="Todos"
            onClick={() => setFiltroTipo("todos")}
            sx={{
              bgcolor: filtroTipo === "todos" ? "#667eea" : "rgba(255,255,255,0.1)",
              color: "white",
              fontWeight: filtroTipo === "todos" ? 700 : 400,
              "&:hover": { bgcolor: "#667eea" },
            }}
          />
          {tiposApoyo.map((tipo) => (
            <Chip
              key={tipo.value}
              label={tipo.label}
              onClick={() => setFiltroTipo(tipo.value)}
              sx={{
                bgcolor: filtroTipo === tipo.value ? tipo.color : "rgba(255,255,255,0.1)",
                color: "white",
                fontWeight: filtroTipo === tipo.value ? 700 : 400,
                "&:hover": { bgcolor: tipo.color },
              }}
            />
          ))}
        </Stack>

        {/* Búsqueda */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <TextField
            placeholder="Buscar por nombre/CURP/dirección/comentarios…"
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                background: "rgba(255,255,255,0.9)",
                borderRadius: 2,
              },
            }}
          />
          <Button variant="contained" onClick={doSearch}>
            Buscar
          </Button>
        </Stack>

        {/* DataGrid */}
        <div
          style={{
            height: isMobile ? 400 : 560,
            width: "100%",
            background: "white",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            rowCount={total}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => setPaginationModel(model)}
            pageSizeOptions={[5, 10, 20, 50]}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(m) => setRowSelectionModel(m)}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: openCreate,
                onEdit: openEdit,
                onDelete: remove,
                onExportExcel: exportToExcel,
              },
            }}
          />
        </div>
      </Box>

      {/* Diálogo Crear/Editar */}
      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? "Editar persona" : "Agregar persona"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre"
                fullWidth
                value={form.nombre}
                onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="CURP (18)"
                type="text"
                fullWidth
                value={form.curp}
                onChange={handleCurpChange}
                inputProps={{ maxLength: 18 }}
                error={Boolean(errors.curp)}
                helperText={errors.curp || ""}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Dirección"
                fullWidth
                value={form.direccion}
                onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                type="tel"
                fullWidth
                value={form.telefono}
                onChange={handleTelefonoChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: TEL_MAX,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={form.email}
                onChange={handleEmailChange}
                error={Boolean(errors.email)}
                helperText={errors.email || ""}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Apoyo</InputLabel>
                <Select
                  value={form.tipo_apoyo}
                  label="Tipo de Apoyo"
                  onChange={(e) => setForm((f) => ({ ...f, tipo_apoyo: e.target.value }))}
                >
                  {tiposApoyo.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Monto (opcional)"
                type="number"
                fullWidth
                value={form.monto}
                onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de apoyo"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha_apoyo}
                onChange={(e) => setForm((f) => ({ ...f, fecha_apoyo: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Comentarios (apoyo otorgado)"
                multiline
                rows={3}
                fullWidth
                value={form.comentarios}
                onChange={(e) => setForm((f) => ({ ...f, comentarios: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!isCurpValid}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{
            minWidth: 280,
            borderRadius: 2,
            boxShadow: "0 10px 24px rgba(0,0,0,.35)",
          }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
